import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Grid,
  ListSubheader,
  MenuItem,
  Paper,
  TextField,
  Typography,
  alpha,
  makeStyles,
} from '@material-ui/core';
import {
  Content,
  Header,
  InfoCard,
  Link,
  MarkdownContent,
  Page,
} from '@backstage/core-components';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { useWikiSections } from '../wiki/useWikiSections';
import {
  ExportAttachment,
  MAX_REQUEST_BYTES,
  ExportPage,
  attachmentSelectable,
  attachmentSelectedByDefault,
  expandZips,
  formatSize,
  readExport,
  splitInBatches,
  toBase64,
} from './jspwikiExport';

const PAGES_PER_COMMIT = 50;
const MAX_ATTACHMENTS_PER_REQUEST = 150;

const STATUS_LABEL: Record<ExportAttachment['status'], string> = {
  referenced: 'Linked from the page',
  unreferenced: 'Not linked',
  duplicate: 'Same content as another file',
  'too-large': 'Over 12 MB',
  blocked: 'File type not imported',
};

const attKey = (page: ExportPage, a: ExportAttachment) =>
  `${page.fileName}/${a.name}`;

const useStyles = makeStyles(theme => ({
  field: { marginBottom: theme.spacing(1.5) },
  drop: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(0.5),
    padding: theme.spacing(2, 2),
    textAlign: 'center',
    cursor: 'pointer',
    borderRadius: theme.shape.borderRadius,
    border: `2px dashed ${theme.palette.primary.main}`,
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
    '&:hover, &.over': {
      backgroundColor: alpha(theme.palette.primary.main, 0.12),
    },
    '&:focus-visible': {
      outline: `3px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
    },
  },
  chips: { marginTop: theme.spacing(1) },
  actions: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  tile: {
    padding: theme.spacing(1, 1.5),
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    height: '100%',
  },
  tileWarn: { borderLeft: `4px solid ${theme.palette.warning.main}` },
  tileSkip: { borderLeft: `4px solid ${theme.palette.text.disabled}` },
  mono: {
    fontFamily: 'monospace',
    fontSize: 13,
    fontWeight: 600,
    overflowWrap: 'anywhere',
  },
  preview: { marginTop: theme.spacing(1) },
  previewBody: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    alignItems: 'flex-start',
    marginTop: theme.spacing(1),
  },
  previewCol: { flex: '1 1 320px', minWidth: 0 },
  raw: {
    margin: 0,
    padding: theme.spacing(1),
    maxHeight: 420,
    overflow: 'auto',
    fontSize: 12,
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
    backgroundColor: alpha(theme.palette.text.primary, 0.04),
    borderRadius: theme.shape.borderRadius,
  },
  note: { color: theme.palette.text.secondary },
  pageBox: {
    padding: theme.spacing(1, 1.5),
    marginBottom: theme.spacing(1),
    borderLeft: `4px solid ${theme.palette.primary.main}`,
  },
  pageHead: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  attRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1),
    paddingLeft: theme.spacing(4),
  },
  error: {
    marginTop: theme.spacing(1.5),
    padding: theme.spacing(1, 1.5),
    borderLeft: `4px solid ${theme.palette.error.main}`,
    backgroundColor: alpha(theme.palette.error.main, 0.06),
  },
  commitRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
  },
}));

interface Result {
  dryRun: boolean;
  section: string;
  sectionCreated: boolean;
  nav: string;
  commitUrl?: string;
  branch?: string;
  bootstrapped?: string[];
  branchCreated?: string;
  entityPath?: string;
  docsPath?: string;
  catalogInfoUrl?: string;
  created: {
    file: string;
    source: string;
    warnings: string[];
    markdown?: string;
  }[];
  skipped: { source: string; reason: string }[];
  attachments?: { file: string; source: string; bytes: number }[];
  commits?: string[];
}

type Running = 'import' | 'preview' | undefined;

export const WikiImportPage = () => {
  const classes = useStyles();
  const discovery = useApi(discoveryApiRef);
  const { fetch } = useApi(fetchApiRef);
  const inputRef = useRef<HTMLInputElement>(null);
  const resultRef = useRef<HTMLDivElement>(null);

  const { sections, defaultLegacyRepo, gitlabBaseUrl: baseUrl } = useWikiSections();
  const [sectionChoice, setSectionChoice] = useState('');
  const [advanced, setAdvanced] = useState(false);
  const [repoPath, setRepoPath] = useState('');
  const folderRef = useRef<HTMLInputElement>(null);
  const [pages, setPages] = useState<ExportPage[]>([]);
  const [pageSel, setPageSel] = useState<Record<string, boolean>>({});
  const [attSel, setAttSel] = useState<Record<string, boolean>>({});
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [withoutPage, setWithoutPage] = useState<string[]>([]);
  const [reading, setReading] = useState(false);
  const [progress, setProgress] = useState<string>();
  const [overwrite, setOverwrite] = useState(false);
  const [allowSensitive, setAllowSensitive] = useState(false);
  const [over, setOver] = useState(false);
  const [running, setRunning] = useState<Running>();
  const [importError, setImportError] = useState<string>();
  const [result, setResult] = useState<Result>();

  const call = async (route: string, body: object) => {
    const base = await discovery.getBaseUrl('wiki-import');
    const r = await fetch(`${base}/${route}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(data.message ?? `Error ${r.status}`);
    return data;
  };

  const onFiles = async (list: FileList | File[] | null) => {
    if (!list || list.length === 0) return;
    setReading(true);
    setImportError(undefined);
    try {
      const reading = await readExport(await expandZips(Array.from(list)));
      const ps: Record<string, boolean> = {};
      const as: Record<string, boolean> = {};
      reading.pages.forEach(pg => {
        ps[pg.fileName] = !pg.almostEmpty;
        pg.attachments.forEach(a => {
          as[attKey(pg, a)] = attachmentSelectedByDefault(a);
        });
      });
      setPages(reading.pages);
      setPageSel(ps);
      setAttSel(as);
      setExpanded({});
      setWithoutPage(reading.withoutPage);
      setResult(undefined);
    } catch (e) {
      setImportError((e as Error).message);
    } finally {
      setReading(false);
    }
  };

  const chosenPages = pages.filter(pg => pageSel[pg.fileName]);
  const chosenAtts = (pg: ExportPage) =>
    pg.attachments.filter(a => attSel[attKey(pg, a)]);
  const totalAtts = chosenPages.reduce((n, pg) => n + chosenAtts(pg).length, 0);
  const totalBytes = chosenPages.reduce(
    (n, pg) => n + chosenAtts(pg).reduce((m, a) => m + a.size, 0),
    0,
  );

  const section = sectionChoice;
  const chosen = sections.find(o => o.name === sectionChoice);
  const sectionTitle = section;
  const overrideRepo = advanced ? repoPath.trim() : '';
  const repoError =
    overrideRepo &&
    !overrideRepo.toLowerCase().startsWith(`${baseUrl.toLowerCase()}/`)
      ? `The URL must start with ${baseUrl}/`
      : undefined;
  const busy = running !== undefined;
  const canPreview = chosenPages.length > 0 && !busy && !reading && !repoError;
  const canImport =
    canPreview && Boolean(section) && (!advanced || Boolean(overrideRepo));

  const run = async (dryRun: boolean) => {
    setImportError(undefined);
    if (!section) {
      setImportError('First choose the wiki section (card 1).');
      return;
    }
    setRunning(dryRun ? 'preview' : 'import');
    const total: Result = {
      dryRun,
      section,
      sectionCreated: false,
      nav: '',
      created: [],
      skipped: [],
      attachments: [],
      commits: [],
    };
    try {
      const groups: ExportPage[][] = [];
      let cur: ExportPage[] = [];
      let curBytes = 0;
      let curAtts = 0;
      for (const pg of chosenPages) {
        const atts = chosenAtts(pg);
        const bytes = atts.reduce((n, a) => n + a.size, 0);
        if (
          cur.length > 0 &&
          (cur.length >= PAGES_PER_COMMIT ||
            curBytes + bytes > MAX_REQUEST_BYTES ||
            curAtts + atts.length > MAX_ATTACHMENTS_PER_REQUEST)
        ) {
          groups.push(cur);
          cur = [];
          curBytes = 0;
          curAtts = 0;
        }
        cur.push(pg);
        curBytes += bytes;
        curAtts += atts.length;
      }
      if (cur.length) groups.push(cur);

      const knownPages = pages.map(p => p.fileName);
      const merge = (r: Result) => {
        total.sectionCreated = total.sectionCreated || r.sectionCreated;
        total.nav = r.nav;
        total.docsPath = r.docsPath ?? total.docsPath;
        total.entityPath = r.entityPath ?? total.entityPath;
        total.catalogInfoUrl = r.catalogInfoUrl ?? total.catalogInfoUrl;
        total.branchCreated = r.branchCreated ?? total.branchCreated;
        total.bootstrapped = [
          ...(total.bootstrapped ?? []),
          ...(r.bootstrapped ?? []),
        ];
        total.created.push(...r.created);
        total.skipped.push(...r.skipped);
        total.attachments?.push(...(r.attachments ?? []));
        if (r.commitUrl) {
          total.commitUrl = r.commitUrl;
          total.commits?.push(r.commitUrl);
        }
      };
      let done = 0;
      for (const group of groups) {
        setProgress(
          `${done + 1}-${done + group.length}/${chosenPages.length} · ${
            group[0].title
          }${group.length > 1 ? ' …' : ''}`,
        );
        if (group.length > 1) {
          const attachments = [];
          for (const pg of group) {
            for (const a of chosenAtts(pg)) {
              attachments.push({
                page: pg.fileName,
                name: a.name,
                content: dryRun ? undefined : await toBase64(a.file),
              });
            }
          }
          merge(
            await call('import', {
              repoPath: overrideRepo || undefined,
              section,
              sectionTitle,
              overwrite,
              allowSensitive,
              dryRun,
              files: group.map(pg => ({
                name: pg.fileName,
                content: pg.content,
              })),
              attachments,
              knownPages,
            }),
          );
        } else {
          const pg = group[0];
          const atts = chosenAtts(pg);
          const batches = dryRun ? [[]] : splitInBatches(atts);
          for (const [b, batch] of (batches.length
            ? batches
            : [[]]
          ).entries()) {
            const inBatch = new Set<ExportAttachment>(batch);
            const attachments = [];
            for (const a of atts) {
              attachments.push({
                page: pg.fileName,
                name: a.name,
                content: inBatch.has(a) ? await toBase64(a.file) : undefined,
              });
            }
            merge(
              await call('import', {
                repoPath: overrideRepo || undefined,
                section,
                sectionTitle,
                overwrite,
                allowSensitive,
                dryRun,
                files:
                  b === 0 ? [{ name: pg.fileName, content: pg.content }] : [],
                attachments,
                knownPages,
              }),
            );
          }
        }
        done += group.length;
        setResult({ ...total });
      }
    } catch (e) {
      setImportError((e as Error).message);
      setResult({ ...total });
    } finally {
      setProgress(undefined);
      setRunning(undefined);
    }
  };

  useEffect(() => {
    if (result)
      resultRef.current?.scrollIntoView?.({
        behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)')
          .matches
          ? 'auto'
          : 'smooth',
        block: 'start',
      });
  }, [result]);

  const missing = !chosenPages.length
    ? 'Choose a folder or pages to import.'
    : !section
    ? 'Choose the wiki section.'
    : advanced && !overrideRepo
    ? 'Enter the repository URL or clear "advanced".'
    : undefined;

  const pickFiles = () => inputRef.current?.click();
  const pickFolder = () => folderRef.current?.click();

  return (
    <Page themeId="tool">
      <Header
        title="Import to the wiki"
        subtitle="Upload wiki pages to publish them as documentation in the DevPortal"
      />
      <Content>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <InfoCard title="1. Section" variant="fullHeight">
              <TextField
                className={classes.field}
                select
                fullWidth
                variant="outlined"
                size="small"
                label="Wiki section"
                value={sectionChoice}
                onChange={e => setSectionChoice(e.target.value)}
                helperText={
                  chosen
                    ? `It will be published at ${chosen.docsPath}`
                    : 'The repository and folder are derived from the section'
                }
              >
                <ListSubheader disableSticky>Wiki sections</ListSubheader>
                {sections.map(o => (
                  <MenuItem key={o.name} value={o.name}>
                    {o.name}
                  </MenuItem>
                ))}
              </TextField>
              <FormControlLabel
                control={
                  <Checkbox
                    color="primary"
                    checked={advanced}
                    onChange={e => setAdvanced(e.target.checked)}
                  />
                }
                label="Use another repository (advanced)"
              />
              {advanced && (
                <TextField
                  className={classes.field}
                  fullWidth
                  variant="outlined"
                  size="small"
                  label="Repository URL in GitLab"
                  value={repoPath}
                  onChange={e => setRepoPath(e.target.value)}
                  error={Boolean(repoError)}
                  helperText={repoError ?? `Example: ${baseUrl}/group/project`}
                  FormHelperTextProps={{
                    role: repoError ? 'alert' : undefined,
                  }}
                />
              )}
              <Typography
                variant="caption"
                display="block"
                className={classes.note}
              >
                Default legacy repository: {defaultLegacyRepo ?? 'loading…'}. Pages are
                published with a direct commit to develop.
              </Typography>
            </InfoCard>
          </Grid>
          <Grid item xs={12} md={6}>
            <InfoCard title="2. Files" variant="fullHeight">
              <input
                ref={inputRef}
                type="file"
                multiple
                accept=".txt,.md,.zip"
                hidden
                onChange={e => onFiles(e.target.files)}
              />
              <input
                ref={folderRef}
                type="file"
                hidden
                {...({ webkitdirectory: '', directory: '' } as object)}
                onChange={e => onFiles(e.target.files)}
              />
              <div
                className={`${classes.drop}${over ? ' over' : ''}`}
                role="button"
                tabIndex={0}
                aria-label="Choose wiki pages (.txt, .md or .zip)"
                onClick={pickFiles}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    pickFiles();
                  }
                }}
                onDragOver={e => {
                  e.preventDefault();
                  setOver(true);
                }}
                onDragLeave={() => setOver(false)}
                onDrop={e => {
                  e.preventDefault();
                  setOver(false);
                  onFiles(e.dataTransfer.files);
                }}
              >
                <Typography variant="subtitle2">
                  Drag the pages here or click to choose them
                </Typography>
                <Typography variant="caption" className={classes.note}>
                  JSPWiki .txt files, .md, or a .zip with pages and their -att
                  folders
                </Typography>
              </div>
              <div className={classes.actions}>
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  disabled={busy || reading}
                  onClick={pickFolder}
                >
                  Choose a folder with attachments
                </Button>
                {reading && <CircularProgress size={18} />}
              </div>
              <FormControlLabel
                control={
                  <Checkbox
                    color="primary"
                    checked={overwrite}
                    onChange={e => setOverwrite(e.target.checked)}
                  />
                }
                label="Overwrite pages that already exist"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    color="primary"
                    checked={allowSensitive}
                    onChange={e => setAllowSensitive(e.target.checked)}
                  />
                }
                label="Allow pages flagged as containing credentials (I have reviewed them)"
              />
              <Typography
                variant="caption"
                display="block"
                className={classes.note}
              >
                Replaces the content of pages that are already in the section.
              </Typography>
              <div className={classes.actions}>
                <Button
                  variant="outlined"
                  color="primary"
                  disabled={!canPreview}
                  onClick={() => run(true)}
                  startIcon={
                    running === 'preview' ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : undefined
                  }
                >
                  {running === 'preview' ? 'Calculating…' : 'Preview'}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={!canImport}
                  onClick={() => run(false)}
                  startIcon={
                    running === 'import' ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : undefined
                  }
                >
                  {running === 'import' ? 'Importing…' : 'Import'}
                </Button>
                <Typography variant="caption" className={classes.note}>
                  {chosenPages.length} page(s) · {totalAtts} attachment(s)
                  {totalAtts ? ` (${formatSize(totalBytes)})` : ''}
                  {progress ? ` · ${progress}` : ''}
                  {!canImport && missing ? ` · ${missing}` : ''}
                </Typography>
              </div>
              {importError && (
                <div className={classes.error} role="alert">
                  <Typography variant="body2">{importError}</Typography>
                </div>
              )}
            </InfoCard>
          </Grid>
          {pages.length > 0 && (
            <Grid item xs={12}>
              <InfoCard title="3. Review what will be imported">
                <Typography variant="body2" className={classes.note} paragraph>
                  Each page is uploaded on its own, with the latest version of
                  its attachments. Attachments linked from the page are selected
                  by default; the others are optional. A single upload never
                  exceeds 12 MB.
                </Typography>
                {withoutPage.length > 0 && (
                  <Typography variant="caption" display="block" paragraph>
                    Attachments without a page (ignored):{' '}
                    {withoutPage.join(', ')}
                  </Typography>
                )}
                {pages.map(pg => {
                  const open =
                    expanded[pg.fileName] ?? pg.attachments.length <= 12;
                  return (
                    <Paper
                      key={pg.fileName}
                      variant="outlined"
                      className={classes.pageBox}
                    >
                      <div className={classes.pageHead}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              color="primary"
                              checked={Boolean(pageSel[pg.fileName])}
                              onChange={e =>
                                setPageSel({
                                  ...pageSel,
                                  [pg.fileName]: e.target.checked,
                                })
                              }
                            />
                          }
                          label={
                            <span className={classes.mono}>{pg.title}</span>
                          }
                        />
                        {pg.sectionHome && (
                          <Chip
                            size="small"
                            color="primary"
                            label="Section home page (replaces the generated index; needs Overwrite)"
                          />
                        )}
                        {pg.almostEmpty && (
                          <Chip size="small" label="Almost empty page" />
                        )}
                        {pg.attachments.length > 0 && (
                          <>
                            <Typography
                              variant="caption"
                              className={classes.note}
                            >
                              {chosenAtts(pg).length} of {pg.attachments.length}{' '}
                              attachments selected
                            </Typography>
                            <Button
                              size="small"
                              onClick={() =>
                                setExpanded({
                                  ...expanded,
                                  [pg.fileName]: !open,
                                })
                              }
                            >
                              {open ? 'Hide' : 'Show'}
                            </Button>
                          </>
                        )}
                      </div>
                      {open &&
                        pg.attachments.map(a => (
                          <div key={a.name} className={classes.attRow}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  color="primary"
                                  size="small"
                                  disabled={!attachmentSelectable(a)}
                                  checked={Boolean(attSel[attKey(pg, a)])}
                                  onChange={e =>
                                    setAttSel({
                                      ...attSel,
                                      [attKey(pg, a)]: e.target.checked,
                                    })
                                  }
                                />
                              }
                              label={
                                <span className={classes.mono}>{a.name}</span>
                              }
                            />
                            <Typography
                              variant="caption"
                              className={classes.note}
                            >
                              {formatSize(a.size)}
                              {a.versions > 1 ? ` · v${a.versions}` : ''}
                              {a.author ? ` · ${a.author}` : ''}
                              {` · ${STATUS_LABEL[a.status]}`}
                              {a.duplicateOf ? ` (${a.duplicateOf})` : ''}
                            </Typography>
                          </div>
                        ))}
                    </Paper>
                  );
                })}
              </InfoCard>
            </Grid>
          )}
          {result && (
            <Grid item xs={12} ref={resultRef} role="status" aria-live="polite">
              <InfoCard title={result.dryRun ? 'Preview' : 'Result'}>
                <Typography variant="body2" className={classes.note} paragraph>
                  Section “{result.section}”
                  {result.sectionCreated ? ' (new)' : ''} ·{' '}
                  {result.created.length} page(s)
                  {result.attachments && result.attachments.length > 0
                    ? ` · ${result.attachments.length} attachment(s)`
                    : ''}
                  {result.dryRun ? '' : ` · ${result.nav}`}
                </Typography>
                {result.commits && result.commits.length > 0 && (
                  <div className={classes.commitRow}>
                    <Typography variant="body2">
                      Imported to develop in {result.commits.length} commit(s).
                    </Typography>
                    {result.commits.map((url, i) => (
                      <Button
                        key={url}
                        variant="outlined"
                        color="primary"
                        size="small"
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Commit {i + 1}
                      </Button>
                    ))}
                  </div>
                )}
                {result.commitUrl && result.docsPath && (
                  <Typography variant="body2" paragraph>
                    The section is available at{' '}
                    <Link to={result.docsPath}>{result.docsPath}</Link>.
                  </Typography>
                )}
                {result.bootstrapped && result.bootstrapped.length > 0 && (
                  <Typography variant="body2" paragraph>
                    This is the first import in this repository: the commit also
                    creates {result.bootstrapped.join(', ')}
                    {result.branchCreated
                      ? ` and the branch ${result.branchCreated} was created`
                      : ''}
                    . Register the component in the catalog with this URL:{' '}
                    <span className={classes.mono}>
                      {result.catalogInfoUrl}
                    </span>{' '}
                    (<Link to="/catalog-import">Register component</Link>).
                  </Typography>
                )}
                <Grid container spacing={2}>
                  {result.created.map(c => (
                    <Grid item xs={12} md={c.markdown ? 12 : 6} key={c.file}>
                      <Paper
                        variant="outlined"
                        className={`${classes.tile}${
                          c.warnings.length ? ` ${classes.tileWarn}` : ''
                        }`}
                      >
                        <Typography className={classes.mono}>
                          {c.file}
                        </Typography>
                        {c.warnings.map(w => (
                          <Typography
                            key={w}
                            variant="caption"
                            display="block"
                            className={classes.note}
                          >
                            Warning: {w}
                          </Typography>
                        ))}
                        {c.markdown !== undefined && (
                          <div className={classes.previewBody}>
                            <div className={classes.previewCol}>
                              <Typography
                                variant="caption"
                                className={classes.note}
                              >
                                How it will look
                              </Typography>
                              <MarkdownContent content={c.markdown} />
                            </div>
                            <div className={classes.previewCol}>
                              <Typography
                                variant="caption"
                                className={classes.note}
                              >
                                Markdown
                              </Typography>
                              <pre className={classes.raw}>{c.markdown}</pre>
                            </div>
                          </div>
                        )}
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
                {result.attachments && result.attachments.length > 0 && (
                  <Typography variant="body2" paragraph>
                    Attachments: {result.attachments.length} file(s) (
                    {formatSize(
                      result.attachments.reduce((n, a) => n + a.bytes, 0),
                    )}
                    ).
                  </Typography>
                )}
                {result.skipped.length > 0 && (
                  <>
                    <Typography
                      variant="subtitle2"
                      style={{ margin: '12px 0 8px' }}
                    >
                      Not imported
                    </Typography>
                    <Grid container spacing={2}>
                      {result.skipped.map(s => (
                        <Grid item xs={12} md={6} key={s.source}>
                          <Paper
                            variant="outlined"
                            className={`${classes.tile} ${classes.tileSkip}`}
                          >
                            <Typography className={classes.mono}>
                              {s.source}
                            </Typography>
                            <Typography
                              variant="caption"
                              display="block"
                              className={classes.note}
                            >
                              {s.reason}
                            </Typography>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  </>
                )}
              </InfoCard>
            </Grid>
          )}
        </Grid>
      </Content>
    </Page>
  );
};
